import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Body7, Container, Dialog, DialogButtonRow } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import {
  companyLabel,
  formatCount,
  formatFeedId,
  formatKnowableAt,
  formatScore,
  formatScoreDelta,
  formatUtcDate,
  payloadEntries,
  scoreDeltaToken,
} from './format';
import { PayloadField } from './PayloadFields';
import { ScoreHistoryChart } from './ScoreHistoryChart';
import { useThemeToken } from './useThemeToken';

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;

  @media (max-width: 620px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Tile = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  padding: 8px 10px;
`;

const TileValue = styled.div`
  font-variant-numeric: tabular-nums;
  padding-top: 2px;
`;

// The occasions, newest first — the order the table reads in when it is sorted by time, and the end
// an analyst starts from. A left rule rather than a filled panel, the same treatment RowPayload uses
// to set prose apart from the grid.
const Occasions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
`;

const Occasion = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 0.9fr) minmax(70px, 0.4fr) minmax(200px, 2fr);
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  align-items: start;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const When = styled.div`
  font-variant-numeric: tabular-nums;
`;

const Score = styled.div`
  font-variant-numeric: tabular-nums;
  text-align: right;

  @media (max-width: 620px) {
    text-align: left;
  }
`;

const meanOf = (scored) => (scored.length ? scored.reduce((total, row) => total + row.score, 0) / scored.length : null);

// A company's score history, as far as this read can see it.
//
// Everything here is already on the rows the table is holding — the occasions, their scores, and the
// score spec's declared payload, which today is reachable only one row at a time through the
// per-row Reasoning disclosure and so cannot be read as a sequence. The panel's whole job is to put
// the numbers and the reasoning behind them in one order.
//
// It states the read's covered span in its own subtitle. Without that line a fifteen-week window
// reads as a company's whole history, which is a claim this page has no way to make.
export const CompanyScoreHistory = ({ series, feedId, covered, onDismiss }) => {
  const themeToken = useThemeToken();

  // Every modal in this app dismisses by overlay click; none of them listens for Escape, and a panel
  // this tall is the first one where reaching the overlay means scrolling. Local rather than pushed
  // into the shared Overlay: changing dismissal for every modal in the app is not this feature's
  // change to make.
  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onDismiss && onDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onDismiss]);

  if (!series || !series.rows.length) return null;

  const latest = series.rows[series.rows.length - 1];
  const label = companyLabel(latest);
  const mean = meanOf(series.scored);
  // The token names the direction and is rendered as `data-direction` below; the hook turns the same
  // token into the colour. Kept as one value so the attribute and the paint cannot disagree.
  const netToken = scoreDeltaToken(series.net);
  const netColor = themeToken(netToken);

  return (
    <Dialog label={`Score history for ${label}`} onDismiss={onDismiss} width="min(720px, 86vw)">
      <Body1 bold>{label}</Body1>
      <Container top={4}>
        <Body7 isLowContrast>
          {formatFeedId(feedId)} ·{' '}
          {covered
            ? `this read covers ${formatUtcDate(covered.from)} → ${formatUtcDate(covered.to)}`
            : 'this read’s covered span is unknown'}{' '}
          — not this company’s whole history.
        </Body7>
      </Container>

      <Container top={16}>
        <Tiles>
          <Tile data-testid="history-latest">
            <Body7 isLowContrast>Latest</Body7>
            <TileValue>
              <Body5 bold>{formatScore(series.last ? series.last.score : null)}</Body5>
            </TileValue>
          </Tile>
          <Tile data-testid="history-average">
            {/* Over the SCORED occasions only. A company scored once in three averages that one
                  score, not a third of it — the rule aggregateByCompany states for the table. */}
            <Body7 isLowContrast>Average</Body7>
            <TileValue>
              <Body5 bold>{formatScore(mean)}</Body5>
            </TileValue>
          </Tile>
          <Tile data-testid="history-net" data-direction={netToken || 'flat'}>
            <Body7 isLowContrast>Net change</Body7>
            <TileValue>
              <Body5 bold color={netColor}>
                {formatScoreDelta(series.net)}
              </Body5>
            </TileValue>
          </Tile>
          <Tile data-testid="history-scored">
            <Body7 isLowContrast>Scored</Body7>
            <TileValue>
              <Body5 bold>
                {formatCount(series.scoredCount)} of {formatCount(series.rows.length)}
              </Body5>
            </TileValue>
          </Tile>
        </Tiles>
      </Container>

      <Container top={16}>
        <ScoreHistoryChart series={series} />
      </Container>

      <Container top={20}>
        <Occasions data-testid="history-occasions">
          {series.rows
            .slice()
            .reverse()
            .map((row) => {
              const entries = payloadEntries(row.payload);
              const unscored = typeof row.score !== 'number' || !isFinite(row.score);
              return (
                <Occasion key={row.key} data-testid="history-occasion" data-unscored={unscored ? 'true' : 'false'}>
                  <When>
                    <Body7>{formatKnowableAt(row.knowableAt)}</Body7>
                  </When>
                  <Score>
                    <Body5 bold={!unscored}>{unscored ? 'no score' : formatScore(row.score)}</Body5>
                  </Score>
                  <div>
                    {entries.length === 0 ? (
                      <Body7 isLowContrast>
                        {unscored
                          ? 'No score at this occasion — the row is kept so the gap is visible.'
                          : 'No reasoning columns in this read.'}
                      </Body7>
                    ) : (
                      entries.map((entry) => (
                        <Container key={entry.key} bottom={6}>
                          <PayloadField entry={entry} maxWidth={70} />
                        </Container>
                      ))
                    )}
                  </div>
                </Occasion>
              );
            })}
        </Occasions>
      </Container>

      <Container top={16}>
        <DialogButtonRow>
          <TransparentFlatButton onClick={onDismiss}>Close</TransparentFlatButton>
        </DialogButtonRow>
      </Container>
    </Dialog>
  );
};
